import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import ImageNext from '@/components/shared/ImageNext';
import styles from './participantCard.module.scss';
import Trans from '@/components/shared/Trans';
import translations from './ParticipantCard.trans.json';
import { useContrastTextColor } from '@/components/shared/Hooks';
import participantMapper from './participantDataMap';
import useParticipantActions from './useParticipantActions';
import useUserProfileLink from '@/hooks/useUserProfileLink';
import resolveTokkoBrokerUrl from '@/utils/resolveTokkoBrokerUrl';

/**
 * Reusable component that renders an information card for contacts or agents.
 * Action buttons are automatically generated based on available data.
 *
 * @component
 * @param {Object} props
 * @param {'contact'|'agent'} [props.type='contact'] - Type of participant to display.
 * @param {Object} props.data - Participant data.
 *
 * **Common props for both types:**
 * @param {string} props.data.id - Unique participant ID (REQUIRED for redirections).
 * @param {string} props.data.name - Full participant name (REQUIRED).
 * @param {string} [props.data.profilePicture] - Profile image URL. Shows placeholder if not provided.
 *
 * **Props specific to type="contact":**
 * @param {string} [props.data.email] - Contact email. If provided, email button will be shown.
 * @param {string} [props.data.leadStatus] - Lead status text (e.g., "Interested", "Contacted").
 * @param {string} [props.data.leadStatusColor='000000'] - Status tag hex color (without #).
 * @param {string} [props.data.agentName] - Assigned agent name.
 * @param {string} [props.data.agentId] - Assigned agent ID (for profile redirection).
 * @param {string} [props.data.isValidCellphone] - Phone validation. If 'not_valid', no WhatsApp button.
 *
 * **Props specific to type="agent":**
 * @param {string} [props.data.branchName] - Agent's branch name.
 *
 * **Automatic button generation:**
 * - **Email**: Shows if `data.email` is present
 * - **WhatsApp**: Shows if `data.isValidCellphone` exists and is not 'not_valid'
 * - **Tokko Chat**: Shows automatically for agents
 *
 * @example
 * // Contact with all data
 * <ParticipantCard
 *   type="contact"
 *   data={{
 *     id: "123",
 *     name: "Juan Pérez",
 *     profilePicture: "https://example.com/avatar.jpg",
 *     email: "juan@example.com",
 *     leadStatus: "Interested",
 *     leadStatusColor: "28a745",
 *     agentName: "María García",
 *     agentId: "456",
 *     isValidCellphone: "valid"
 *   }}
 * />
 *
 * @example
 * // Basic agent
 * <ParticipantCard
 *   type="agent"
 *   data={{
 *     id: "789",
 *     name: "Carlos López",
 *     branchName: "Centro Branch"
 *   }}
 * />
 *
 * @example
 * // Minimal contact (only Tokko chat button will be shown)
 * <ParticipantCard
 *   type="contact"
 *   data={{
 *     id: "321",
 *     name: "Ana Martínez"
 *   }}
 * />
 */

const defaultParticipantProps = {
  agent: {
    id: '',
    name: '',
    branchName: '',
  },
  contact: {
    id: '',
    name: '',
    profilePicture: '',
    email: '',
    leadStatus: '',
    leadStatusColor: '000000',
    agentName: '',
    agentId: '',
  },
};

const renderContactData = (
  participantData,
  handleProfileRedirect,
  getUserProfileLink,
  textColor,
  isWhiteLeadStatusColor
) => (
  <Box className={styles.participantData}>
    <Text
      backgroundColor={`#${participantData.leadStatusColor}`}
      variant="body.fontSize12"
      className={`${styles.leadStatusBadge} ${
        isWhiteLeadStatusColor ? styles.leadStatusBadgeWhite : ''
      }`}
      color={textColor}
    >
      {participantData.leadStatus}
    </Text>
    <Text
      onClick={() => handleProfileRedirect(`/contact/${participantData.id}`)}
      className={styles.contactName}
    >
      {participantData.name}
    </Text>
    <Text
      onClick={() =>
        handleProfileRedirect(getUserProfileLink(participantData.agentId))
      }
      className={styles.agentCharge}
    >
      {participantData.agentName && (
        <>
          {Trans(translations, 'Agent')} {participantData.agentName}
        </>
      )}
    </Text>
  </Box>
);

const renderAgentData = (
  participantData,
  handleProfileRedirect,
  getUserProfileLink
) => (
  <Box className={styles.participantData}>
    <Text
      onClick={() =>
        handleProfileRedirect(getUserProfileLink(participantData.id))
      }
      className={styles.agentName}
    >
      {participantData.name}
    </Text>
    <Text className={styles.agentBranch}>{participantData.branchName}</Text>
  </Box>
);

const buttonRender = (button) => {
  const buttonContent = button.src ? (
    <ImageNext
      key={button.id}
      src={button.src}
      name={button.icon || button.id}
      alt={button.alt || button.id}
    />
  ) : (
    <Icon key={button.id} icon={button.icon} fontSize="20px" />
  );

  return buttonContent;
};

const normalizeHexColor = (color) =>
  String(color || '')
    .trim()
    .replace(/^#/, '')
    .toLowerCase();

const isWhiteHexColor = (color) => {
  const normalized = normalizeHexColor(color);
  if (!normalized) return false;
  if (normalized === 'fff' || normalized === 'ffffff') return true;
  return false;
};

function ParticipantCard({ type = 'contact', data = defaultParticipantProps }) {
  const getUserProfileLink = useUserProfileLink();
  // Custom hook that handles all action logic and state
  const { handleButtonClick, isButtonDisabled, ownerID } =
    useParticipantActions(data, type);
  if (!data) return null;

  // Use mapper with custom hook handlers
  const participantData =
    participantMapper[type]?.(data, {
      handleButtonClick,
      isButtonDisabled,
      ownerID,
    }) || defaultParticipantProps[type];

  const actionButtons = participantData.actionButtons || [];
  const picture = participantData.profilePicture || '';

  const handleProfileRedirect = (profile) => {
    window.open(resolveTokkoBrokerUrl(profile), '_blank');
  };

  const { textColor } = useContrastTextColor(
    `#${participantData?.leadStatusColor || '000000'}`
  );
  const isWhiteLeadStatusColor = isWhiteHexColor(
    participantData?.leadStatusColor
  );

  const renderMapping = {
    contact: () =>
      renderContactData(
        participantData,
        handleProfileRedirect,
        getUserProfileLink,
        textColor,
        isWhiteLeadStatusColor
      ),
    agent: () =>
      renderAgentData(
        participantData,
        handleProfileRedirect,
        getUserProfileLink
      ),
  };

  const renderDataSection = renderMapping[type] || (() => null);

  const buttonsAction = () => (
    <Box className={styles.buttonsContainer}>
      {actionButtons.map((button, index) => (
        <Box __css={{ display: 'flex' }} key={button.id}>
          {/* eslint-disable-next-line react/self-closing-comp */}
          {index > 0 && <Box className={styles.separator}></Box>}
          <Box
            onMouseDown={(e) => {
              // Prevent parent containers from handling press/click and redirecting.
              e.stopPropagation();
            }}
            onClick={(e) => {
              // Prevent parent containers from handling click and redirecting.
              e.preventDefault();
              e.stopPropagation();
              if (button.disabled) return;
              button.onClick?.(e);
            }}
            className={`${styles.buttonAction} ${
              button.disabled ? styles.disabled : ''
            }`}
            style={{
              opacity: button.disabled ? 0.5 : 1,
              cursor: button.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {buttonRender(button)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const placeholderIconType = {
    contact: 'icon-contactos',
    agent: 'icon-agente',
  };

  return (
    <Box className={`${styles.participantCard} ${styles[type]}`}>
      <Box className={styles.leftContent}>
        {picture ? (
          <ImageNext
            className={styles.avatarImage}
            width={56}
            height={56}
            alt="participant"
            src={picture}
          />
        ) : (
          <Box className={styles.avatarPlaceholder}>
            <Icon icon={placeholderIconType[type]} />
          </Box>
        )}
        {renderDataSection()}
      </Box>
      <Box
        className={styles.rightContent}
        onMouseDown={(e) => {
          // Prevent parent containers from handling press/click and redirecting.
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Prevent parent containers from handling click and redirecting.
          e.stopPropagation();
        }}
      >
        {buttonsAction()}
      </Box>
    </Box>
  );
}

export default ParticipantCard;
